import React, { useContext } from "react";
import { Box, Typography, Container } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import ThemeToggle from "./ThemeToggle";
import UserContext from "./UserContext";

const BRAND = { navy: "#0a0e27", navyDark: "#0f1233", accent: "#637cdb" };

const SECTIONS = [
  {
    title: "1. Acceptance of Terms",
    body: `By accessing or using the Proove AI platform ("Service"), operated by Proove Genomics ("Proove," "we," "us," or "our"), you agree to be bound by these Terms of Use ("Terms"). If you do not agree to these Terms in their entirety, you may not access or use the Service. These Terms apply to all users, including patients, licensed healthcare providers, and any other individuals who access the Service.`,
  },
  {
    title: "2. Eligibility",
    body: `You must be at least 18 years of age to use this Service. By using the Service, you represent and warrant that you are 18 years of age or older and have the legal capacity to enter into a binding agreement. Healthcare provider accounts require a valid National Provider Identifier (NPI) and are subject to additional verification.`,
  },
  {
    title: "3. Description of Service",
    body: `Proove AI provides AI-powered analysis of laboratory reports through two tools:\n\n• AIden — Patient Assistant: Designed for patients to receive plain-language explanations of Proove lab results, general wellness guidance, and questions to ask their healthcare providers.\n\n• ClAIre — Clinical Decision Support: Designed for licensed healthcare professionals to receive evidence-based clinical analysis, risk stratification, and peer-reviewed citations based on Proove patient reports.\n\nOpioid Risk and Pain Perception analysis is limited to Proove Genomics' own laboratory reports (old or new format), as no other laboratory produces these panels. Drug Metabolism/PGX analysis additionally supports reports from other laboratories, subject to selecting the correct report source at upload; analysis of reports from other providers may produce incomplete or inaccurate results.`,
  },
  {
    title: "4. Medical Disclaimer",
    body: `THE SERVICE IS NOT A SUBSTITUTE FOR PROFESSIONAL MEDICAL ADVICE, DIAGNOSIS, OR TREATMENT.\n\n• AIden outputs are intended solely for general wellness and informational purposes and do not constitute medical advice.\n\n• ClAIre outputs are intended as clinical decision support aids for licensed healthcare professionals and do not replace independent clinical judgment.\n\nAlways consult a qualified healthcare provider before making medical decisions. In a medical emergency, call 911 or your local emergency number immediately. Proove AI does not provide emergency medical services.`,
  },
  {
    title: "5. Account Registration and Security",
    body: `You are responsible for maintaining the confidentiality of your account credentials and for all activity that occurs under your account. You agree to:\n\n• Provide accurate, current, and complete information during registration.\n• Promptly update your account information if it changes.\n• Notify Proove immediately at proove.com if you suspect unauthorized access to your account.\n• Not share your login credentials with any third party.\n\nProove reserves the right to suspend or terminate accounts that violate these Terms or that exhibit suspicious activity.`,
  },
  {
    title: "6. Acceptable Use",
    body: `You agree not to use the Service to:\n\n• Upload reports belonging to individuals for whom you do not have proper authorization.\n• Attempt to reverse-engineer, decompile, or extract the underlying AI models or proprietary algorithms.\n• Submit false, misleading, or fraudulent information.\n• Circumvent any security or access controls.\n• Use the Service for any unlawful purpose or in violation of any applicable laws or regulations.\n• Reproduce, distribute, or publicly display outputs for commercial purposes without prior written consent from Proove.`,
  },
  {
    title: "7. Intellectual Property",
    body: `The Service, including all software, algorithms, AI models, user interface designs, logos, text, and graphics, is the exclusive property of Proove Genomics and its licensors, protected by applicable copyright, trademark, and intellectual property laws. You are granted a limited, non-exclusive, non-transferable, revocable license to access and use the Service for its intended purpose. No rights are granted to you other than as expressly set forth in these Terms.`,
  },
  {
    title: "8. Payments and Refunds",
    body: `Access to certain features of the Service (including the patient-facing AIden tool) may require payment of a one-time or recurring fee. All fees are in U.S. dollars. Payments are processed securely through our third-party payment processor. Fees are non-refundable except as required by applicable law or as determined at Proove's sole discretion on a case-by-case basis. Proove reserves the right to modify pricing at any time with reasonable notice.`,
  },
  {
    title: "9. Privacy and HIPAA Compliance",
    body: `Proove is committed to protecting the privacy and security of patient health information. Our collection, use, and disclosure of protected health information (PHI) is governed by our Privacy Policy and applicable provisions of the Health Insurance Portability and Accountability Act of 1996 (HIPAA) and its implementing regulations. Patient names and dates of birth are stored only as cryptographic hashes (SHA-256) and are never transmitted to third-party AI providers. Please review our Privacy Policy at proove.com/privacy for full details.`,
  },
  {
    title: "10. Limitation of Liability",
    body: `TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, PROOVE GENOMICS AND ITS OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, AND LICENSORS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, OR GOODWILL, ARISING OUT OF OR RELATED TO YOUR USE OR INABILITY TO USE THE SERVICE, EVEN IF PROOVE HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.\n\nProove's aggregate liability for any claim arising out of or related to the Service shall not exceed the total amount paid by you to Proove in the twelve (12) months preceding the event giving rise to the claim.`,
  },
  {
    title: "11. Disclaimers",
    body: `THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, ACCURACY, OR NON-INFRINGEMENT. PROOVE DOES NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR FREE FROM HARMFUL COMPONENTS. AI-generated outputs may contain errors and should always be verified by a qualified professional.`,
  },
  {
    title: "12. Governing Law and Dispute Resolution",
    body: `These Terms shall be governed by and construed in accordance with the laws of the State of Delaware, United States, without regard to its conflict of law provisions. Any dispute arising out of or relating to these Terms or the Service that cannot be resolved through good-faith negotiation shall be submitted to binding arbitration in accordance with the rules of the American Arbitration Association. Notwithstanding the foregoing, either party may seek injunctive or other equitable relief in any court of competent jurisdiction.`,
  },
  {
    title: "13. Changes to Terms",
    body: `Proove reserves the right to modify these Terms at any time. Material changes will be communicated via email or a prominent notice on the Service at least 14 days before taking effect. Your continued use of the Service after the effective date of any revision constitutes your acceptance of the updated Terms. We encourage you to review these Terms periodically.`,
  },
  {
    title: "14. Termination",
    body: `Proove may suspend or terminate your access to the Service immediately, without prior notice, for any reason, including if you breach these Terms. Upon termination, your right to use the Service will immediately cease. Sections 4, 7, 10, 11, 12, and 15 of these Terms shall survive termination.`,
  },
  {
    title: "15. Contact",
    body: `If you have questions about these Terms of Use, please contact Proove Genomics through our website at proove.com. We are committed to addressing your inquiries in a timely manner.`,
  },
];

export default function TermsOfUse() {
  const theme = useTheme();
  const { user } = useContext(UserContext);
  const isDark = theme.palette.mode === "dark";
  const bg = isDark ? "#080d1a" : "#f4f7ff";
  const cardBg = isDark ? "#111827" : "#ffffff";
  const textPrimary = isDark ? "#e8edf5" : "#0d1b2e";
  const textSecondary = isDark ? "#94a3b8" : "#4a5568";
  const border = isDark ? "#1e2d50" : "#e2e8f0";
  // Reached both pre-login (from the Login page) and from inside the app
  // (a logged-in user's footer link) — the logo/back link must never dump
  // an already-logged-in user back onto the login form, which reads as an
  // unexpected logout even though their session is untouched.
  const backTo = user?.token ? "/home" : "/";
  const backLabel = user?.token ? "← Back to Home" : "← Back to Sign In";

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: bg, display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box sx={{
        bgcolor: isDark ? "rgba(2,6,15,0.95)" : "rgba(10,14,39,0.97)",
        borderBottom: "2px solid rgba(99,124,219,0.3)",
        flexShrink: 0,
      }}>
        <Box sx={{ maxWidth: 1200, mx: "auto", px: 3, height: 64,
          display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box component={Link} to={backTo} sx={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
            <img src="/logo-light.png" alt="Proove" style={{ height: 20, width: "auto" }} />
          </Box>
          <ThemeToggle />
        </Box>
      </Box>

      {/* Content */}
      <Container maxWidth="md" sx={{ py: 8, flex: 1 }}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          {/* Title block */}
          <Box sx={{ mb: 6 }}>
            <Typography sx={{
              fontSize: { xs: 12, sm: 13 }, fontWeight: 700, letterSpacing: "0.12em",
              color: BRAND.accent, mb: 1.5, textTransform: "uppercase",
            }}>
              Legal
            </Typography>
            <Typography sx={{
              fontSize: { xs: 32, sm: 42 }, fontWeight: 800,
              color: textPrimary, lineHeight: 1.15, mb: 2, letterSpacing: "-0.02em",
            }}>
              Terms of Use
            </Typography>
            <Typography sx={{ fontSize: 16, color: textSecondary, lineHeight: 1.75, maxWidth: 600 }}>
              These Terms of Use govern your access to and use of the Proove AI platform operated by Proove Genomics.
            </Typography>
            <Box sx={{
              mt: 3, display: "inline-flex", gap: 1.5, flexWrap: "wrap",
            }}>
              <Box sx={{
                fontSize: 13, color: textSecondary,
                bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(10,14,39,0.05)",
                border: `1px solid ${border}`, borderRadius: "8px", px: 2, py: 0.75,
              }}>
                Effective date: May 1, 2025
              </Box>
              <Box sx={{
                fontSize: 13, color: textSecondary,
                bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(10,14,39,0.05)",
                border: `1px solid ${border}`, borderRadius: "8px", px: 2, py: 0.75,
              }}>
                Last updated: May 25, 2026
              </Box>
            </Box>
          </Box>

          {/* Sections */}
          <Box sx={{
            bgcolor: cardBg,
            border: `1px solid ${border}`,
            borderRadius: "20px",
            overflow: "hidden",
            boxShadow: isDark ? "0 8px 40px rgba(0,0,0,0.35)" : "0 8px 40px rgba(10,14,39,0.08)",
          }}>
            {SECTIONS.map((section, idx) => (
              <Box key={idx} sx={{
                px: { xs: 3, sm: 5 }, py: 3.5,
                borderBottom: idx < SECTIONS.length - 1 ? `1px solid ${border}` : "none",
              }}>
                <Typography sx={{
                  fontSize: 17, fontWeight: 700, color: isDark ? "#f1f5f9" : BRAND.navy,
                  mb: 1.5, lineHeight: 1.35,
                }}>
                  {section.title}
                </Typography>
                {section.body.split("\n\n").map((para, i) => (
                  <Typography key={i} component="p" sx={{
                    fontSize: 15, color: textSecondary, lineHeight: 1.85, mb: 1.5,
                    "&:last-child": { mb: 0 },
                    whiteSpace: "pre-line",
                  }}>
                    {para}
                  </Typography>
                ))}
              </Box>
            ))}
          </Box>

          {/* Footer nav */}
          <Box sx={{ mt: 6, display: "flex", gap: 3, flexWrap: "wrap", justifyContent: "center" }}>
            <Box component={Link} to="/privacy"
              sx={{ fontSize: 14, color: BRAND.accent, textDecoration: "underline", fontWeight: 600 }}>
              Privacy Policy
            </Box>
            <Box component={Link} to={backTo}
              sx={{ fontSize: 14, color: textSecondary, textDecoration: "none",
                "&:hover": { color: BRAND.navy } }}>
              {backLabel}
            </Box>
          </Box>
        </motion.div>
      </Container>

      {/* Footer */}
      <Box sx={{
        bgcolor: isDark ? "rgba(2,6,15,0.9)" : BRAND.navyDark,
        py: 2.5, textAlign: "center",
        borderTop: "1px solid rgba(255,255,255,0.06)",
      }}>
        <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.3)", letterSpacing: "0.04em" }}>
          © {new Date().getFullYear()} Proove Genomics. All rights reserved. &nbsp;·&nbsp; proove.com
        </Typography>
        <Typography sx={{ fontSize: 10, color: "rgba(255,255,255,0.18)", letterSpacing: "0.03em", mt: 0.5 }}>
          Powered by{" "}
          <Box component="a" href="https://labarium.com/" target="_blank" rel="noopener noreferrer"
            sx={{ color: "rgba(255,255,255,0.2)", textDecoration: "none", "&:hover": { color: "rgba(255,255,255,0.4)" } }}>
            Labarium Diagnostic Systems
          </Box>
        </Typography>
      </Box>
    </Box>
  );
}
